import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function AdminAnswered() {
  const [prayers, setPrayers] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ status: "Answer Acknowledged", answer_text: "", notify_holders: true });

  const load = useCallback(() => {
    api.get("/admin/prayers", { params: { moderation_status: "approved" } }).then(({ data }) => setPrayers(data.prayers));
  }, []);
  useEffect(load, [load]);

  const recordAnswer = async () => {
    await api.post(`/admin/prayers/${active.id}/answer`, form);
    toast("Answer recorded. Moved to the register.");
    setActive(null);
    load();
  };
  const feature = async (p) => {
    await api.patch(`/admin/prayers/${p.id}/feature`, { featured: !p.featured });
    load();
  };

  const answered = prayers.filter((p) => ["Answer Acknowledged", "Answered Differently", "Withdrawn With Gratitude"].includes(p.status));
  const open = prayers.filter((p) => !["Answer Acknowledged", "Answered Differently", "Withdrawn With Gratitude"].includes(p.status));
  const inputCls = "w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none";

  return (
    <div className="p-6 md:p-10">
      <p className="mono-label mb-3">The Register</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Answered Prayers</h1>

      <h2 className="font-heading text-2xl text-ink mt-10 mb-4">Record an answer</h2>
      <div className="border border-ink/15">
        {open.map((p) => (
          <div key={p.id} className="p-4 border-b border-ink/10 flex items-center justify-between gap-4 bg-paper" data-testid={`answerable-${p.id}`}>
            <div><div className="font-heading text-lg text-ink">{p.display_name || p.title}</div><span className="mono-label">{p.category} · {p.status}</span></div>
            <button onClick={() => { setActive(p); setForm({ status: "Answer Acknowledged", answer_text: p.answer_text || "", notify_holders: true }); }} className="btn-ink" data-testid={`record-${p.id}`}>Record answer</button>
          </div>
        ))}
        {open.length === 0 && <div className="p-8 text-center font-body text-ink-tertiary italic">No open prayers.</div>}
      </div>

      <h2 className="font-heading text-2xl text-ink mt-12 mb-4">In the register ({answered.length})</h2>
      <div className="border border-ink/15">
        {answered.map((p) => (
          <div key={p.id} className="p-4 border-b border-ink/10 flex items-center justify-between gap-4 bg-paper" data-testid={`answered-${p.id}`}>
            <div><span className="mono-label">No. {String(p.prayer_number || 0).padStart(6, "0")}</span><div className="font-heading text-lg text-ink">{p.display_name}</div><span className="stamp">{p.status}</span></div>
            <button onClick={() => feature(p)} className="mono-label underline underline-offset-4" data-testid={`feature-${p.id}`}>{p.featured ? "Unfeature" : "Feature"}</button>
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="answer-modal">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setActive(null)} />
          <div className="relative bg-paper border border-ink/25 max-w-lg w-full p-8">
            <h3 className="font-heading text-2xl text-ink mb-2">{active.display_name || active.title}</h3>
            <p className="font-body text-ink-secondary mb-6 leading-relaxed">{active.prayer_text}</p>
            <label className="block mb-4"><span className="mono-label block mb-2">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="answer-status">
                <option>Answer Acknowledged</option><option>Answered Differently</option><option>Withdrawn With Gratitude</option>
              </select></label>
            <label className="block mb-4"><span className="mono-label block mb-2">Answer / update text</span>
              <textarea rows={4} value={form.answer_text} onChange={(e) => setForm({ ...form, answer_text: e.target.value })} className={inputCls} data-testid="answer-text" /></label>
            <label className="flex items-center gap-2 mb-6 cursor-pointer">
              <input type="checkbox" checked={form.notify_holders} onChange={(e) => setForm({ ...form, notify_holders: e.target.checked })} className="w-4 h-4 accent-ink" data-testid="answer-notify" />
              <span className="font-body text-ink-secondary">Notify members who hold this prayer</span></label>
            <div className="flex gap-3">
              <button onClick={recordAnswer} className="btn-solid" data-testid="answer-save">Move to register</button>
              <button onClick={() => setActive(null)} className="btn-ink">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminUpdates() {
  const [updates, setUpdates] = useState([]);
  const load = useCallback(() => api.get("/admin/updates", { params: { moderation_status: "new" } }).then(({ data }) => setUpdates(data.updates)), []);
  useEffect(load, [load]);

  const moderate = async (u, action, excerpt) => {
    await api.patch(`/admin/updates/${u.id}`, { action, public_excerpt: excerpt, notify_holders: true });
    toast(action === "approve" ? "Update approved." : "Update rejected.");
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <p className="mono-label mb-3">Timeline Review</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Updates</h1>
      <p className="font-body text-ink-secondary mt-4 leading-relaxed">Updates from submitters require review before they are published or shared with holders.</p>
      <div className="mt-8 space-y-4">
        {updates.map((u) => <UpdateRow key={u.id} u={u} moderate={moderate} />)}
        {updates.length === 0 && <div className="border border-ink/15 p-12 text-center font-body text-ink-tertiary italic">No updates awaiting review.</div>}
      </div>
    </div>
  );
}

function UpdateRow({ u, moderate }) {
  const [excerpt, setExcerpt] = useState(u.update_text);
  return (
    <div className="border border-ink/15 p-5 bg-paper" data-testid={`update-${u.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="mono-label">{u.prayer_title} · No. {u.prayer_number ? String(u.prayer_number).padStart(6, "0") : "—"}</span>
        <span className="stamp">{u.requested_status}</span>
      </div>
      <p className="font-body text-ink-secondary leading-relaxed">Original: {u.update_text}</p>
      <textarea rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} className="w-full bg-transparent border border-ink/25 px-3 py-2 mt-3 font-body text-ink outline-none" data-testid={`update-excerpt-${u.id}`} />
      <div className="flex gap-3 mt-3">
        <button onClick={() => moderate(u, "approve", excerpt)} className="btn-solid" data-testid={`update-approve-${u.id}`}>Approve & publish</button>
        <button onClick={() => moderate(u, "reject")} className="btn-ink" data-testid={`update-reject-${u.id}`}>Reject</button>
      </div>
    </div>
  );
}

export function AdminPeople() {
  const [people, setPeople] = useState([]);
  const [offers, setOffers] = useState([]);
  useEffect(() => {
    api.get("/admin/people").then(({ data }) => setPeople(data.people));
    api.get("/admin/service-offers").then(({ data }) => setOffers(data.offers));
  }, []);
  return (
    <div className="p-6 md:p-10">
      <p className="mono-label mb-3">The Circle</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">People</h1>

      {offers.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-2xl text-ink mb-4">Offers to help</h2>
          <div className="border border-ink/15">
            {offers.map((o) => (
              <div key={o.id} className="p-4 border-b border-ink/10 bg-paper" data-testid={`offer-${o.id}`}>
                <span className="mono-label">{o.user_name} · {o.user_email}</span>
                <p className="font-body text-ink mt-1">{o.message || "Offered to help."}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 overflow-x-auto border border-ink/15">
        <table className="w-full text-left min-w-[600px]">
          <thead><tr className="bg-paper-tertiary mono-label"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Submitted</th><th className="p-3">Holding</th></tr></thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} className="border-t border-ink/10 font-body text-ink bg-paper" data-testid={`person-${p.id}`}>
                <td className="p-3">{p.name}</td><td className="p-3">{p.email}</td><td className="p-3">{p.role}</td><td className="p-3">{p.prayers_submitted}</td><td className="p-3">{p.prayers_held}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
